import React, { useCallback } from 'react';
import { SamplesTrialUpsell, useEligibleForFreeTrial } from '@landr/pricing';
import {
    Modal,
    Flex,
    Text,
    Button,
    Group,
    Paragraph,
    Progress,
    RemoveOutlined,
} from '@landr/maestro';
import { Trans } from '@lingui/macro';
import { ModalProps, ModalCloseHandler } from 'modals/Modal.types';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import { useExecutionEnvironmentContext } from 'contexts/ExecutionEnvironmentContext/ExecutionEnvironmentContext';
import { usePricingConfig } from 'hooks/usePricingConfig';

const NotEligibleToTrial = ({
    handleClose,
}: {
    handleClose: ModalCloseHandler;
}) => {
    const { isDAWPlugin } = useExecutionEnvironmentContext();
    const { isAuthenticated } = useAuthentication();
    const { authenticatedPricingPath, guestPricingPath } = usePricingConfig();

    const path = isAuthenticated ? authenticatedPricingPath : guestPricingPath;

    const handleGetLandrStudio = useCallback(() => {
        isDAWPlugin
            ? window.open(path, '_blank')
            : window.location.assign(path);
    }, [path, isDAWPlugin]);

    return (
        <Group direction="vertical" mb="jb">
            <Group.Item textAlign="center">
                <RemoveOutlined size="xl" />
            </Group.Item>
            <Group.Item>
                <Paragraph textAlign="center">
                    <Text textStyle="title-sm">
                        <Trans>
                            Oops! Looks like you’ve already tried LANDR.
                        </Trans>
                    </Text>
                </Paragraph>
                <Paragraph textAlign="center">
                    <Text textStyle="lead" color="text.medium">
                        <Trans>
                            Free trials are only available for new users who
                            have never purchased a LANDR subscription.
                        </Trans>
                    </Text>
                </Paragraph>
            </Group.Item>
            <Group.Item>
                <Group justifyContent="center">
                    <Group.Item>
                        <Button onClick={handleGetLandrStudio} isRounded>
                            <Trans>Get LANDR Studio</Trans>
                        </Button>
                    </Group.Item>
                    <Group.Item>
                        <Button
                            onClick={() => handleClose()}
                            variant="ghost"
                            isRounded
                        >
                            <Trans> Back to app</Trans>
                        </Button>
                    </Group.Item>
                </Group>
            </Group.Item>
        </Group>
    );
};

export const LoadingState = () => (
    <Flex justifyContent="center" alignItems="center" minHeight="50vh">
        <Progress />
    </Flex>
);

const TrialUpsellModalContent = () => (
    <Group direction="vertical" pb="md" gap="md">
        <Group.Item gap="md">
            <SamplesTrialUpsell />
        </Group.Item>
        <Group.Item>
            <Text textStyle="body-sm" color="text.medium" textAlign="center">
                <Trans>
                    Trial period - You can only download samples listed as free
                    or premium giveaway. MP3 master include audio watermark.
                    Activate your subscription at any time to remove these
                    limitations.
                </Trans>
            </Text>
        </Group.Item>
    </Group>
);

export const TrialUpsellModal: React.FC<ModalProps> = ({
    handleClose,
    isOpen,
}) => {
    const { isEligibleForTrial, loading: isLoading } =
        useEligibleForFreeTrial();

    const isTrial = !isLoading && isEligibleForTrial;
    const isNotEligible = !isLoading && !isEligibleForTrial;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            theme="dark"
            size={isNotEligible ? 'md' : 'lg'}
        >
            <Modal.Header>
                {isTrial && (
                    <Modal.Title>
                        <Trans>Choose your trial</Trans>
                    </Modal.Title>
                )}
            </Modal.Header>
            <Modal.Content>
                <Modal.Section py={!isLoading ? 'none' : '3xl'}>
                    {isLoading && <LoadingState />}
                    {isTrial && <TrialUpsellModalContent />}
                    {isNotEligible && (
                        <NotEligibleToTrial handleClose={handleClose} />
                    )}
                </Modal.Section>
            </Modal.Content>
        </Modal>
    );
};

export default TrialUpsellModal;
